import { getClientInfosFromServer } from "@onesolution/serverFn";
import { BoxCcgAuth, BoxClient } from "box-typescript-sdk-gen";
import { InMemoryTokenStorage } from "box-typescript-sdk-gen/lib/box/tokenStorage.generated";
import type { FetchResponse } from "box-typescript-sdk-gen/lib/networking/fetchResponse.generated";
import type { SerializedData } from "box-typescript-sdk-gen/lib/serialization/json";

const BoxSDKAuth = new BoxCcgAuth({
	config: {
		clientId: import.meta.env.VITE_BOX_CLIENT_ID,
		clientSecret: import.meta.env.VITE_BOX_CLIENT_SECRET,
		enterpriseId: import.meta.env.VITE_BOX_ENTERPRISE_ID,
		userId: import.meta.env.VITE_BOX_USER_ID,
		tokenStorage: new InMemoryTokenStorage({}),
	},
});

export const Box = {
	sdk: BoxSDKAuth,
	entrepriseSdk: BoxSDKAuth.withEnterpriseSubject(
		import.meta.env.VITE_BOX_ENTERPRISE_ID,
	),
	async getToken(
		type: "user" | "enterprise",
		userId = import.meta.env.VITE_BOX_USER_ID,
	) {
		if (type === "enterprise") {
			return (await this.entrepriseSdk.retrieveToken()).accessToken;
		}

		if (type === "user" && userId)
			return (await BoxSDKAuth.withUserSubject(userId).retrieveToken())
				.accessToken;

		return "";
	},
};

type BoxSDKWithCustom = BoxClient & {
	users: {
		findUserByMondayId: (mondayUserId: string) => Promise<string>;
		create: (data: SerializedData) => Promise<FetchResponse>;
		refreshGroup: (boxUserId: string) => Promise<boolean>;
	};
};

export const BoxSDK = new BoxClient({ auth: BoxSDKAuth }) as BoxSDKWithCustom;

BoxSDK.users.findUserByMondayId = async (mondayUserId: string) => {
	const rep = await BoxSDK.users.getUsers({
		externalAppUserId: mondayUserId,
	});
	const boxUserId = rep.entries?.[0]?.id;

	if (!boxUserId)
		throw `can't find any box user with monday user id '${mondayUserId}'`;

	return boxUserId;
};

const url = "https://api.box.com/2.0";

// because box.users.createUser don't handle app user param
BoxSDK.users.create = async (data: SerializedData) =>
	BoxSDK.makeRequest({ method: "POST", url: `${url}/users`, data });

BoxSDK.users.refreshGroup = async (boxUserId: string) => {
	try {
		const { boxGroupId } = await getClientInfosFromServer();
		console.log("user_syncrefresh_box_group", boxUserId, boxGroupId);
		if (!boxGroupId || !boxUserId) {
			console.error(`Can't find box group id in client configuration`);
			return false;
		}

		const { entries: userGroups } =
			await BoxSDK.memberships.getUserMemberships(boxUserId);
		const userAlreadyInGroup = userGroups?.some(
			({ group }) => group?.id === boxGroupId,
		);
		const groupMembershipIdsToRemove =
			userGroups
				?.filter(({ group }) => group?.id !== boxGroupId)
				.map(({ id }) => id || "")
				.filter(Boolean) || [];

		if (groupMembershipIdsToRemove.length) {
			await Promise.all(
				groupMembershipIdsToRemove.map((id) =>
					BoxSDK.memberships.deleteGroupMembershipById(id),
				),
			);
		}

		if (!userAlreadyInGroup) {
			await BoxSDK.memberships.createGroupMembership({
				user: { id: boxUserId },
				group: {
					id: boxGroupId,
				},
			});
		}

		console.log("user_syncrefresh_box_group_success", boxUserId, boxGroupId);

		return true;
	} catch (e) {
		console.error(
			`Issue when refreshing box user groups (box user id: ${boxUserId})`,
			e,
		);

		return false;
	}
};
